import {
  RejectReason,
  RejectedFile,
  buildAcceptAttribute,
  isAllowedUploadExtension,
  isValidUploadSize,
} from '../utils/FileUpload.logic';

// 図枠ファイルの追加欄は図枠ファイルと関連画像の両方を受け付け、関連画像の追加欄は画像だけを受け付ける
export const FRAME_ZONE_ACCEPT = [buildAcceptAttribute('frame-drawing'), buildAcceptAttribute('frame-image')].join(',');
export const FRAME_IMAGE_ZONE_ACCEPT = buildAcceptAttribute('frame-image');

export interface FrameFileClassification {
  frame: File | null;
  images: File[];
  rejected: RejectedFile[];
}

type FrameFileCategory = 'unsupported' | 'too_large' | 'frame' | 'image';

// 拡張子と容量だけでファイルの区分を決める
function toFrameFileCategory(file: File): FrameFileCategory {
  const isFrame = isAllowedUploadExtension('frame-drawing', file.name);
  const isImage = isAllowedUploadExtension('frame-image', file.name);
  if (!isFrame && !isImage) {
    return 'unsupported';
  }
  if (!isValidUploadSize(file.size)) {
    return 'too_large';
  }
  if (isFrame) {
    return 'frame';
  }
  return 'image';
}

// 図枠ファイルとして採用する位置。既に添付済みのときと、図枠ファイルが2件以上あるときは採用しないので -1
function decideFrameIndex(categories: FrameFileCategory[], frameAlreadyAttached: boolean): number {
  if (frameAlreadyAttached) {
    return -1;
  }
  const frameCount = categories.filter((category) => category === 'frame').length;
  if (frameCount !== 1) {
    return -1;
  }
  return categories.indexOf('frame');
}

function decideExtraFrameReason(frameAlreadyAttached: boolean): RejectReason {
  if (frameAlreadyAttached) {
    return 'frame_already_attached';
  }
  return 'extra_frame';
}

// 関連画像は、図枠ファイルが既に添付済みか、同じときに図枠ファイルを1件だけ入れたときに採用する。null なら採用する
function decideImageRejectReason(
  categories: FrameFileCategory[],
  frameAlreadyAttached: boolean,
  frameIndex: number
): RejectReason | null {
  if (frameAlreadyAttached || frameIndex >= 0) {
    return null;
  }
  const frameCount = categories.filter((category) => category === 'frame').length;
  if (frameCount >= 2) {
    return 'extra_frame';
  }
  return 'images_require_frame';
}

// 入れたファイルを、図枠ファイル・関連画像・添付できなかったファイルに分ける
export function classifyFrameFiles(files: File[], frameAlreadyAttached: boolean): FrameFileClassification {
  const categories = files.map(toFrameFileCategory);
  const frameIndex = decideFrameIndex(categories, frameAlreadyAttached);
  const images: File[] = [];
  const rejected: RejectedFile[] = [];
  for (let i = 0; i < files.length; i++) {
    const category = categories[i];
    if (category === 'unsupported' || category === 'too_large') {
      rejected.push({ file: files[i], reason: category });
    } else if (category === 'frame') {
      if (i !== frameIndex) {
        rejected.push({ file: files[i], reason: decideExtraFrameReason(frameAlreadyAttached) });
      }
    } else {
      const reason = decideImageRejectReason(categories, frameAlreadyAttached, frameIndex);
      if (reason === null) {
        images.push(files[i]);
      } else {
        rejected.push({ file: files[i], reason });
      }
    }
  }
  let frame: File | null = null;
  if (frameIndex >= 0) {
    frame = files[frameIndex];
  }
  return { frame, images, rejected };
}
